import { type AxiosError } from 'axios'
import { makeAutoObservable } from 'mobx'

import { DmsController } from '@/app/api'
import { type IApiErrorHandler } from '@/app/types/common'
import {
    type IInsured,
    type ICompany,
    type IConpanyContract,
    type ICompanyPrograms,
    type IAddContractDto,
    type IUpdateContractDto,
    type IAddCompanyDto,
    type IUpdateCompanyDto,
    type IAddProgramDto,
    type IUpdateProgramDto,
    type IChosenInsured,
    type ICreateInsuredDto,
    type IUpdateInsuredDto,
    type IInsuredFilters,
    type IImportHistory,
    type IPostProgramAttachRangeDto,
    type IPropgramDatesRange,
    type IPostSpecialConditionDto,
    type IPostSpecialConditionAttachRangeDto,
    type IInsuranceCompanyPolicyHolders,
    type IGuideMarker,
    type IChosenCompany,
    type ITransferPoliciesDto,
    type ITransfetIds
} from '@/app/types/dmsTypes'
import { buildFioFilter, onHandleResponseNotification, onUseQueries } from '@/app/utils'

import { onUseOdataQueries3 } from './helper'

const initialInsuredFilters: IInsuredFilters = {
    insuranceCompanyName: null,
    insuredFio: null,
    insuredPolisNumber: null,
    markers: null,
    expirationDates: { startDate: null, endDate: null },
    skip: 0,
    top: 10,
    totalCount: 0
}

class Dms {
    // common
    // filters: IDmsFilters = defaultDmsFilters
    importHistory: IImportHistory[] | null = null
    guideMarkers: IGuideMarker[] = []
    isFetching: boolean = false

    // insureds
    insuredsList: IInsured[] | null = null
    insuredFilters: IInsuredFilters = initialInsuredFilters
    chosenInsured: IChosenInsured | null = null

    // companies
    companiesList: ICompany[] | null = null
    chosenCompany: IChosenCompany | null = null
    companiesFilters: any = {}

    // contracts
    companyContracts: IConpanyContract[] | null = null
    transferableContractTargetIds: ITransfetIds = { newСontractId: null, oldСontractId: null }

    // programs
    companyPrograms: ICompanyPrograms | null = null

    constructor() {
        makeAutoObservable(this)
    }

    // actions
    // common
    onSetImportHistory = (importHistory: IImportHistory[] | null) => {
        this.importHistory = importHistory
    }

    onSetGuideMarkers = (guideMarkers: IGuideMarker[]) => {
        this.guideMarkers = guideMarkers.sort((a, b) => (a.sequence > b.sequence ? 1 : -1))
    }

    onZeroDmsData = () => {
        this.onSetInsuredsList(null)
        this.onSetCompaniesList(null)
        this.onSetChosenInsured(null)
        this.onSetChosenCompany(null)
        this.onSetCompanyContracts(null)
        this.onSetCompanyPrograms(null)
        this.onSetImportHistory(null)
        this.onSetGuideMarkers([])
    }

    onSetIsFetching = (isFetching: boolean) => {
        this.isFetching = isFetching
    }

    onUseQueryFilters() {
        const { insuredFio, insuredPolisNumber, insuranceCompanyName, expirationDates, markers, top, skip } =
            this.insuredFilters

        const insuredNameFilter = buildFioFilter(insuredFio)

        const insuredPolisNumberFilter = insuredPolisNumber
            ? `(contains(tolower(polisNumber),'${insuredPolisNumber.toLowerCase()}'))`
            : ''

        const insuranceCompanyNameFilter = insuranceCompanyName
            ? `(contains(tolower(insuranceCompanyName),'${insuranceCompanyName.toLowerCase()}'))`
            : ''

        const startDate = expirationDates.startDate ? `${expirationDates.startDate}T00:00:00.000Z` : null
        const endDate = expirationDates.endDate ? `${expirationDates.endDate}T23:59:59.000Z` : null

        const ditesFilter = `((expirationDate gt ${startDate} or expirationDate eq ${startDate}) and (expirationDate lt ${endDate} or expirationDate eq ${endDate}))`

        const markersList = markers ? markers.split(',') : []

        const markersFilter = markers
            ? markersList.map((marker) => `identificationMarkers/any(marker: marker/code eq '${marker}')`).join(' or ')
            : ''

        return onUseOdataQueries3({
            filters: [
                {
                    filterString: insuredNameFilter,
                    isExist: insuredFio ? insuredFio.length > 0 : false,
                    conditionParam: 'and'
                },
                {
                    filterString: insuredPolisNumberFilter,
                    isExist: insuredPolisNumber ? insuredPolisNumber.length > 0 : false,
                    conditionParam: 'and'
                },
                {
                    filterString: insuranceCompanyNameFilter,
                    isExist: insuranceCompanyName ? insuranceCompanyName.length > 0 : false,
                    conditionParam: 'and'
                },
                // {
                //     filterString: policyExpirationDateFilter,
                //     isExist: policyExpirationDate ? policyExpirationDate.length > 0 : false,
                //     conditionParam: 'and'
                // },
                { filterString: ditesFilter, isExist: !!startDate && !!endDate, conditionParam: 'and' },
                {
                    filterString: markersFilter,
                    isExist: markers ? markers.length > 0 : false,
                    conditionParam: 'and'
                }
            ],
            sortindFiled: 'f asc',
            top,
            skip,
            count: true
        })
    }

    // insureds
    onSetInsuredsList = (insuredsList: IInsured[] | null) => {
        this.insuredsList = insuredsList
    }

    onSetChosenInsured = (chosenInsured: IChosenInsured | null) => {
        this.chosenInsured = chosenInsured
    }

    onSetInsuredFilters = (insuredFilters: IInsuredFilters) => {
        this.insuredFilters = insuredFilters
    }

    onResetInsuredsFilters = () => {
        this.onSetInsuredFilters(initialInsuredFilters)
    }

    // companies
    onSetCompaniesList = (companiesList: ICompany[] | null) => {
        this.companiesList = companiesList
    }

    onSetChosenCompany = (chosenCompany: IChosenCompany | null) => {
        this.chosenCompany = chosenCompany
    }

    onSetCompaniesFilters = (companiesFilters: any) => {
        this.companiesFilters = companiesFilters
    }

    // contracts
    onSetCompanyContracts = (companyContracts: IConpanyContract[] | null) => {
        this.companyContracts = companyContracts
    }

    onSetTransferableContractTargetIds = (transferableContractTargetIds: ITransfetIds) => {
        this.transferableContractTargetIds = transferableContractTargetIds
    }

    // programs
    onSetCompanyPrograms = (companyPrograms: ICompanyPrograms | null) => {
        this.companyPrograms = companyPrograms
    }

    // requests
    onGetImportInfoById = async (key: string, onGetErrors: (errors: string[]) => void) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        try {
            const queries = onUseQueries({ id: key })
            const { data, status } = await client.onGetImportInfoById(queries)
            if (status >= 200 && status < 300) {
                onGetErrors(data.errors)
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onAddImportTemplate = async (
        contractId: string,
        isAttacheTemplate: boolean,
        isAddedAttach: boolean,
        file: File
    ) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        try {
            const queries = onUseQueries({
                contractId,
                isAttacheTemplate: isAttacheTemplate ? 'true' : 'false',
                isAddedAttach: isAddedAttach ? 'true' : 'false'
            })
            const dto = new FormData()
            dto.append('file', file)

            await client.onAddImportTemplate(queries, dto)
        } catch (e) {
            // TODO: пофиксить по человечески
            // const error = e as AxiosError<{
            //     isSuccess: boolean
            //     successText: string
            //     errors: string[]
            // }>
            const error = e as any

            // Распарсим detail, если он есть
            if (error.response?.data?.detail) {
                try {
                    // Пытаемся распарсить как JSON
                    const detail = JSON.parse(error.response.data.detail)
                    if (detail?.error) {
                        onHandleResponseNotification(error.response?.status || 500, detail.error)
                        return
                    }
                } catch {
                    // Если не JSON, используем как есть
                    if (error.response?.data?.detail?.error) {
                        onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail?.error)
                        return
                    }
                }
            }

            onHandleResponseNotification(error.response?.status || 500, 'Не удалось выполнить импорт')
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onGetImportTemplate = async (isAttacheTemplate: boolean) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        const queries = onUseQueries({ isAttacheTemplate: isAttacheTemplate ? 'true' : 'false' })
        try {
            const { data } = await client.onGetImportTemplate(queries)

            const cleanBase64 = data.base64.replace(/^data:.+;base64,/, '')

            const byteCharacters = atob(cleanBase64)
            const byteNumbers = new Array(byteCharacters.length)

            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i)
            }

            const byteArray = new Uint8Array(byteNumbers)
            const blob = new Blob([byteArray], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            })

            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')

            link.href = url
            link.download = data.fileName.endsWith('.xlsx') ? data.fileName : `${data.fileName}.xlsx`
            document.body.appendChild(link)
            link.click()

            document.body.removeChild(link)
            URL.revokeObjectURL(url)
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    // insureds
    onGetInsuredsList = async () => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        const queries = this.onUseQueryFilters()
        try {
            const { data } = await client.onGetInsuredsList(queries)
            this.onSetInsuredFilters({ ...this.insuredFilters, totalCount: data['@odata.count'] })
            this.onSetInsuredsList(data.value)
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onGetChosenInsured = async (polisId: string) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        const queries = onUseQueries({ polisId })
        try {
            const { data } = await client.onGetChosenInsured(queries)
            this.onSetChosenInsured({ edit: data })
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onAddInsured = async (dto: ICreateInsuredDto, onSuccess: (newInsuredId: string) => void) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        try {
            const { status, data } = await client.onAddInsured(dto)
            if (status < 300 && status >= 200) {
                onHandleResponseNotification(status, 'Пациент успешно добавлен')
                onSuccess(data)
                this.onGetInsuredsList()
            }
        } catch (e: any) {
            if (e?.response?.data?.errors?.BirthDate[0] && e.response?.status) {
                const error = e?.response?.data?.errors?.BirthDate[0]
                onHandleResponseNotification(e.response?.status || 500, error)
            } else {
                const error = e as AxiosError<IApiErrorHandler>
                onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
            }
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onSaveInsuredChanges = async (
        dto: IUpdateInsuredDto,
        companyId: string,
        onSuccess?: (newInsuredId: string) => void
    ) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        try {
            const { status, data } = await client.onSaveInsuredChanges(dto)
            if (data && status < 300 && status >= 200) {
                onHandleResponseNotification(status, 'Пациент успешно обновлен')
                // this.onSetChosenInsured(null)
                this.onGetInsuredsList()
                onSuccess?.(data)
            }
        } catch (e: any) {
            if (e?.response?.data?.errors?.BirthDate[0] && e.response?.status) {
                const error = e?.response?.data?.errors?.BirthDate[0]
                onHandleResponseNotification(e.response?.status || 500, error)
            } else {
                const error = e as AxiosError<IApiErrorHandler>
                onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
            }
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onRemoveInsured = async (polisId: string, companyId: string, onSuccess: () => void) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        const queries = onUseQueries({ polisId })
        try {
            const { status } = await client.onRemoveInsured(queries)
            if (status < 300 && status >= 200) {
                onHandleResponseNotification(status, 'Пациент успешно удален')
                this.onSetChosenInsured(null)
                this.onSetInsuredFilters({ ...this.insuredFilters, skip: 0, top: 10, totalCount: 0 })
                this.onGetInsuredsList()
                onSuccess?.()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onGetImportHistory = async () => {
        const client = new DmsController()
        try {
            const { data } = await client.onGetImportHistory()
            this.onSetImportHistory(data)
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    onGetGuideMarkers = async () => {
        const client = new DmsController()
        try {
            const { data } = await client.onGetGuideMarkers()
            this.onSetGuideMarkers(data)
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    // companies
    onGetCompaniesList = async () => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        try {
            const { data } = await client.onGetCompaniesList()
            this.onSetCompaniesList(data)
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onGetChosenCompany = async (companyId: string, type: string) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        try {
            const { data } = await client.onGetCompaniesList()
            const foundCompany = data.find((company: ICompany) => company.id === companyId)
            if (foundCompany) {
                this.onSetChosenCompany({ [type]: foundCompany })
            } else {
                onHandleResponseNotification(500, 'Some error has occured...')
                this.onSetChosenCompany(null)
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onAddCompany = async (dto: IAddCompanyDto, onSuccess: (newCompanyId: string) => void) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        try {
            const { status, data } = await client.onAddCompany(dto)
            if (status < 300 && status >= 200) {
                onHandleResponseNotification(status, 'Компания успешно добавлена')
                this.onGetCompaniesList()
                onSuccess(data)
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onSaveCompanyChanges = async (dto: IUpdateCompanyDto, onSuccess?: () => void) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        try {
            const { status } = await client.onSaveCompanyChanges(dto)
            if (status < 300 && status >= 200) {
                onHandleResponseNotification(status, 'Компания успешно обновлена')
                this.onGetCompaniesList()
                onSuccess?.()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onRemoveCompany = async (id: string, onSuccess?: () => void) => {
        this.onSetIsFetching(true)
        const queries = onUseQueries({ removedId: id })
        const client = new DmsController()
        try {
            const { status } = await client.onRemoveCompany(queries)
            if (status < 300 && status >= 200) {
                onHandleResponseNotification(status, 'Компания успешно удалена')
                this.onGetCompaniesList()
                onSuccess?.()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onGetPolicyHoldersByCompanyId = async (
        id: string,
        page: number,
        filter: string,
        onSuccess: (insuranceCompanyPolicyHolders: IInsuranceCompanyPolicyHolders) => void
    ) => {
        this.onSetIsFetching(true)
        const queries = onUseQueries({ insuranceCompanyId: id, page, filter })
        const client = new DmsController()
        try {
            const { data, status } = await client.onGetPolicyHoldersByCompanyId(queries)
            if (status < 300 && status >= 200) {
                onSuccess({ ...data, insuranceCompanyId: id })
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    // contracts
    onGetCompanyContracts = async (id: string) => {
        this.onSetIsFetching(true)
        const queries = onUseQueries({ insuranceCompanyId: id })
        const client = new DmsController()
        try {
            const { data } = await client.onGetCompanyContracts(queries)
            this.onSetCompanyContracts(data)
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onAddContract = async (dto: IAddContractDto, onSuccess?: () => void) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        try {
            const { status } = await client.onAddContract(dto)
            if (status < 300 && status >= 200) {
                onHandleResponseNotification(status, 'Договор успешно добавлен')
                this.onGetCompanyContracts(dto.insuranceCompanyId)
                onSuccess?.()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onUpdateContract = async (dto: IUpdateContractDto, onSuccess?: () => void) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        try {
            const { status } = await client.onUpdateContract(dto)
            if (status < 300 && status >= 200) {
                onHandleResponseNotification(status, 'Договор успешно обновлен')
                onSuccess?.()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onRemoveContract = async (contractId: string, insuranceCompanyId: string, onSuccess?: () => void) => {
        this.onSetIsFetching(true)
        const queries = onUseQueries({ removedId: contractId })
        const client = new DmsController()
        try {
            const { status } = await client.onRemoveContract(queries)
            if (status < 300 && status >= 200) {
                onHandleResponseNotification(status, 'Договор успешно удалён')
                this.onGetCompanyContracts(insuranceCompanyId)
                onSuccess?.()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onTransferPolicies = async (
        dto: ITransferPoliciesDto,
        onSuccess: () => void,
        onError: (message: string) => void
    ) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        try {
            const { status } = await client.onTransferPolicies(dto)
            if (status < 300 && status >= 200) {
                onHandleResponseNotification(status, 'Список успешно перенесен')
                onSuccess?.()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            if (error.response?.status === 404 && typeof error.response?.data?.detail === 'string') {
                onError(error.response?.data?.detail)
            } else {
                onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
            }
        } finally {
            this.onSetIsFetching(false)
        }
    }

    // programs
    onGetCompanyPrograms = async (contractId: string) => {
        this.onSetIsFetching(true)
        const queries = onUseQueries({ contractId })
        const client = new DmsController()
        try {
            const { data } = await client.onGetCompanyPrograms(queries)
            this.onSetCompanyPrograms({ contractId, data })
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onAddProgram = async (dto: IAddProgramDto, onSuccess?: () => void) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        try {
            const { status } = await client.onAddProgram(dto)
            if (status < 300 && status >= 200) {
                onHandleResponseNotification(status, 'Программа успешно добавлена')
                this.onGetCompanyPrograms(dto.contractId)
                onSuccess?.()
            }
        } catch (e: any) {
            if (e?.response?.data?.errors?.Name[0] && e.response?.status) {
                const error = e?.response?.data?.errors?.Name[0]
                onHandleResponseNotification(e.response?.status || 500, error)
            } else {
                const error = e as AxiosError<IApiErrorHandler>
                onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
            }
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onUpdateProgram = async (dto: IUpdateProgramDto, onSuccess?: () => void) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        try {
            const { status } = await client.onUpdateProgram(dto)
            if (status < 300 && status >= 200) {
                onHandleResponseNotification(status, 'Программа успешно обновлена')
                onSuccess?.()
            }
        } catch (e: any) {
            if (e?.response?.data?.errors?.Name[0] && e.response?.status) {
                const error = e?.response?.data?.errors?.Name[0]
                onHandleResponseNotification(e.response?.status || 500, error)
            } else {
                const error = e as AxiosError<IApiErrorHandler>
                onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
            }
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onRemoveProgram = async (programId: string, contractId: string, onSuccess?: () => void) => {
        this.onSetIsFetching(true)
        const queries = onUseQueries({ removedId: programId })
        const client = new DmsController()
        try {
            const { status } = await client.onRemoveProgram(queries)
            if (status < 300 && status >= 200) {
                onHandleResponseNotification(status, 'Программа успешно удалена')
                this.onGetCompanyPrograms(contractId)
                onSuccess?.()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onRemoveProgramAttachRange = async (removedId: string, polisId: string, onSuccess: () => void) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        const queries = onUseQueries({ removedId })
        try {
            const { status } = await client.onRemoveProgramAttachRange(queries)
            if (status >= 200 && status < 300) {
                onHandleResponseNotification(status, 'Программа успешно откреплена')
                this.onSetChosenInsured(null)
                this.onGetChosenInsured(polisId)
            }
            onSuccess()
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onPostProgramAttachRange = async (
        dto: IPostProgramAttachRangeDto,
        onSuccess?: (incomingRange: IPropgramDatesRange) => void
    ) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        try {
            const { data, status } = await client.onPostProgramAttachRange(dto)
            if (status >= 200 && status < 300) {
                this.onSetChosenInsured(null)
                this.onGetChosenInsured(dto.polisId)
                onSuccess?.({ startedAt: dto.startedAt, finishedAt: dto.finishedAt, id: data })
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onPostSpecialCondition = async (dto: IPostSpecialConditionDto, onSuccess: (id: string) => void) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        try {
            const { data, status } = await client.onPostSpecialCondition(dto)
            if (status >= 200 && status < 300) {
                onSuccess(data)
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onPostSpecialConditionAttachRange = async (
        dto: IPostSpecialConditionAttachRangeDto,
        onSuccess?: (incomingRange: IPropgramDatesRange) => void
    ) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        try {
            const { data } = await client.onPostSpecialConditionAttachRange(dto)
            onSuccess?.({ startedAt: dto.startedAt, finishedAt: dto.finishedAt, id: data })
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onRemoveSpecialConditionAttachRange = async (
        specialConditionProgramId: string,
        polisId: string,
        onSuccess: () => void
    ) => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        const queries = onUseQueries({ specialConditionProgramId })
        try {
            const { status } = await client.onRemoveSpecialConditionAttachRange(queries)
            if (status >= 200 && status < 300) {
                onHandleResponseNotification(status, 'Расширение успешно откреплено')
                // this.onSetChosenInsured(null)
                // this.onGetChosenInsured(polisId)
            }
            onSuccess()
        } catch (e) {
        } finally {
            this.onSetIsFetching(false)
        }
    }

    onGetLastErrors = async () => {
        this.onSetIsFetching(true)
        const client = new DmsController()
        try {
            const { data } = await client.onGetLastErrors()

            const cleanBase64 = data.base64.replace(/^data:.+;base64,/, '')

            const byteCharacters = atob(cleanBase64)
            const byteNumbers = new Array(byteCharacters.length)

            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i)
            }

            const byteArray = new Uint8Array(byteNumbers)
            const blob = new Blob([byteArray], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            })

            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')

            link.href = url
            link.download = data.fileName.endsWith('.xlsx') ? data.fileName : `${data.fileName}.xlsx`
            document.body.appendChild(link)
            link.click()

            document.body.removeChild(link)
            URL.revokeObjectURL(url)
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetching(false)
        }
    }
}

export default new Dms()
